import React, { useCallback, useEffect } from 'react';
import { useOrgChartQuery } from '../queries/businessProfileQueries';
import { OrgChartNode, OwnerType } from '../types';
import { OrgChart } from '../components/OrgChart';
import { StepStatus } from '../components/StepStatus';
import { RegisterStep } from '../wizard/stepApi';

interface ReviewStepProps {
    businessProfileId: number | null;
    onEditNode: (node: OrgChartNode) => void; // Jumps back to the step corresponding to the item the user clicked
    onRegister: RegisterStep;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
    businessProfileId,
    onEditNode,
    onRegister,
}) => {
    const { data: root, isLoading, isError, error, refetch } = useOrgChartQuery(businessProfileId);

    // Everything is already persisted; Save Organization just completes the wizard
    // TODO: When server API is implemented, we need to maintain an "is in progress?" flag, and flip it here
    const save = useCallback(async () => undefined, []);

    useEffect(() => {
        onRegister({ save, canAdvance: !!root });
    }, [save, root, onRegister]);

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    if (!root) {
        return (
            <div className='bp-step__placeholder'>
                Nothing to review yet — add a business and a borrower entity first.
            </div>
        );
    }

    return (
        <div className='bp-step bp-step--review'>
            <OrgChart
                root={root}
                onEdit={onEditNode}
                onDelete={(node) => {
                    // Only owners are deletable; the umbrella business and first entity are not
                    // TODO: Implement logic to allow deletion of *other* entities - i.e., a sibling entity, or a sub-owner
                    if (node.nodeType !== OwnerType.Person && node.nodeType !== OwnerType.Entity) {
                        return;
                    }
                    onEditNode(node);
                }}
            />
        </div>
    );
};
